import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronDown,
  ClipboardCheck,
  Coffee,
  FileText,
  Plus,
  Refrigerator,
  RotateCcw,
  Save,
  ShieldCheck,
  Snowflake,
  Trash2,
  Thermometer,
  Wind,
} from 'lucide-react';
import { useEffectiveMe } from '@/lib/effectiveMe';
import { canApproveHaccp } from './haccpAccess';
import {
  approveHaccpConfiguration,
  createHaccpTemperatureTemplate,
  createHaccpDraft,
  getHaccpTemperatureRules,
  getHaccpTemperatureTemplates,
  returnHaccpConfiguration,
  saveHaccpConfiguration,
  saveHaccpTemperatureRules,
  submitHaccpConfiguration,
  updateHaccpTemperatureTemplate,
  useHaccpConfigurations,
} from './useHaccpConfigurations';
import PositiveBlastChillingRulesEditor from './PositiveBlastChillingRulesEditor';
import NegativeBlastChillingRulesEditor from './NegativeBlastChillingRulesEditor';
import {
  TEMPERATURE_LIMIT_BASES,
  TEMPERATURE_PROFILE_BY_CODE,
  isTemperatureRuleComplete,
  profilesForEquipment,
  temperatureModeLabel,
} from './temperatureProfiles';
import './haccp.css';

function formatTemperature(value) {
  if (value === '' || value === null || value === undefined || !Number.isFinite(Number(value))) {
    return null;
  }
  const numericValue = Number(value);
  const formatted = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 }).format(Math.abs(numericValue));
  const sign = numericValue < 0 ? '−' : numericValue > 0 ? '+' : '';
  return `${sign}${formatted}°C`;
}

function temperatureRuleSummary(rule) {
  if (rule.limit_mode === 'MAX_ONLY') {
    return formatTemperature(rule.max_temperature_c)
      ? `Massimo ${formatTemperature(rule.max_temperature_c)}`
      : 'Massimo da definire';
  }
  if (rule.limit_mode === 'MIN_ONLY') {
    return formatTemperature(rule.min_temperature_c)
      ? `Minimo ${formatTemperature(rule.min_temperature_c)}`
      : 'Minimo da definire';
  }
  const minimum = formatTemperature(rule.min_temperature_c);
  const maximum = formatTemperature(rule.max_temperature_c);
  return minimum && maximum ? `${minimum} / ${maximum}` : 'Valori da completare';
}

function temperatureRuleDisplay(rule) {
  if (rule.limit_mode === 'MAX_ONLY') {
    const operationalMinimum = formatTemperature(rule.operational_target_min_temperature_c);
    const operationalMaximum = formatTemperature(rule.operational_target_max_temperature_c);
    if (operationalMinimum && operationalMaximum) {
      return `${operationalMinimum} / ${operationalMaximum}`;
    }
  }
  return temperatureRuleSummary(rule);
}

const MODULES = [
  { code: 'TEMPERATURES', title: 'Temperature', detail: 'Controlli previsti per le attrezzature del freddo', Icon: Snowflake },
  { code: 'BLAST_CHILLING_POSITIVE', title: 'Abbattimento positivo', detail: 'Attività previste per l’abbattimento positivo', Icon: Wind },
  { code: 'BLAST_CHILLING_NEGATIVE', title: 'Abbattimento negativo', detail: 'Attività previste per l’abbattimento negativo', Icon: Snowflake },
];

const EQUIPMENT_KINDS = [
  { value: 'FRIDGE', label: 'Frigorifero', Icon: Refrigerator },
  { value: 'FREEZER', label: 'Congelatore', Icon: Snowflake },
  { value: 'COLD_ROOM', label: 'Cella frigorifera', Icon: Snowflake },
  { value: 'BLAST_CHILLER', label: 'Abbattitore', Icon: Wind },
  { value: 'OTHER', label: 'Altro', Icon: Coffee },
];

function temperatureRulePayload(rule) {
  const mode = rule.limit_mode || 'RANGE';
  const decimalOrNull = (value) => value === '' || value == null ? null : value;
  return {
    limit_mode: mode,
    min_temperature_c: mode === 'MAX_ONLY' || rule.min_temperature_c === ''
      ? null : rule.min_temperature_c,
    max_temperature_c: mode === 'MIN_ONLY' || rule.max_temperature_c === ''
      ? null : rule.max_temperature_c,
    tolerance_c: rule.tolerance_c === '' ? null : rule.tolerance_c,
    profile_code: rule.profile_code || null,
    operational_target_min_temperature_c: decimalOrNull(rule.operational_target_min_temperature_c),
    operational_target_max_temperature_c: decimalOrNull(rule.operational_target_max_temperature_c),
    limit_basis: rule.limit_basis || null,
    basis_reference: rule.basis_reference?.trim() || null,
    check_times_local: rule.check_times_text
      .split(',').map((value) => value.trim()).filter(Boolean),
    late_after_minutes: Number(rule.late_after_minutes),
    instructions: rule.instructions.trim() || null,
    anomaly_note_required: rule.anomaly_note_required,
    measurement_method: rule.measurement_method,
    corrective_instructions: rule.corrective_instructions,
    verification_after_minutes: Number(rule.verification_after_minutes),
  };
}

const STATUS_LABELS = {
  ACTIVE: 'Attiva',
  DRAFT: 'Bozza',
  IN_REVIEW: 'In revisione',
  SUPERSEDED: 'Sostituita',
  NOT_CONFIGURED: 'Da configurare',
};

function formatDate(value) {
  if (!value) return 'Data non disponibile';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Data non disponibile';
  return new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}

function StateBadge({ state }) {
  const label = STATUS_LABELS[state] || STATUS_LABELS.NOT_CONFIGURED;
  const active = state === 'ACTIVE';
  const draft = state === 'DRAFT';
  const review = state === 'IN_REVIEW';
  return (
    <span
      className={`haccp-state-badge ${active ? 'is-active' : ''} ${draft ? 'is-draft' : ''} ${review ? 'is-review' : ''}`}
      data-testid={`haccp-state-${state || 'NOT_CONFIGURED'}`}
    >
      <span className="haccp-state-dot" />
      {label}
    </span>
  );
}

function HaccpLoading() {
  return (
    <div className="haccp-page" aria-label="Caricamento configurazioni HACCP">
      <div className="haccp-skeleton haccp-skeleton-heading" />
      <div className="haccp-skeleton haccp-skeleton-banner" />
      {[0, 1, 2].map((item) => <div className="haccp-skeleton haccp-skeleton-row" key={item} />)}
    </div>
  );
}

function HaccpError({ error, retry }) {
  const tenantMissing = error?.status === 409;
  return (
    <section className="haccp-state-panel" role="alert">
      <span className="haccp-state-icon haccp-state-icon-error"><AlertCircle size={22} /></span>
      <div>
        <h2>{tenantMissing ? 'Seleziona un’azienda' : 'Configurazioni non disponibili'}</h2>
        <p>{tenantMissing
          ? 'Per consultare i locali, seleziona prima un’azienda dal contesto della Dashboard.'
          : 'Controlla la connessione e riprova. Se il problema continua, contatta l’amministratore.'}</p>
        <button type="button" className="haccp-button haccp-button-primary mt-4" onClick={retry}>
          <RotateCcw size={16} /> Riprova
        </button>
      </div>
    </section>
  );
}

function activeModuleNames(configuration) {
  const codes = new Set(
    (configuration?.modules || []).filter((module) => module.selected).map((module) => module.code),
  );
  const overviewNames = {
    TEMPERATURES: 'Temperature',
    TRACEABILITY_LABELS: 'Etichette di tracciabilità',
    BLAST_CHILLING_POSITIVE: 'Abbattimento positivo',
    BLAST_CHILLING_NEGATIVE: 'Abbattimento negativo',
    THAWING: 'Scongelamento',
    CLEANING: 'Pulizia',
    EXPIRY: 'Scadenze',
    OIL: 'Olio di frittura',
    GOODS_RECEIVING: 'Ricevimento merci',
  };
  return [...codes].map((code) => overviewNames[code] || code);
}

export function HaccpOverview() {
  const { locations, loading, error, reload } = useHaccpConfigurations();
  const me = useEffectiveMe();
  const canApprove = canApproveHaccp(me);
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState('');
  const activeCount = locations.filter((location) => location.active_configuration).length;
  const pendingCount = locations.filter((location) => location.draft_configuration).length;

  const reviewAction = async (location, action) => {
    const pending = location.draft_configuration;
    if (!pending || busyId) return;
    setBusyId(location.location_id);
    setActionError('');
    try {
      const operation = action === 'approve' ? approveHaccpConfiguration : returnHaccpConfiguration;
      await operation(location.location_id, pending.id, pending.revision);
      await reload();
    } catch (requestError) {
      setActionError(requestError?.status === 409
        ? 'La configurazione è cambiata. Aggiorna la pagina prima di riprovare.'
        : 'Operazione non completata. Riprova.');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <HaccpLoading />;

  return (
    <main className="haccp-page" data-testid="haccp-overview">
      <header className="haccp-page-header">
        <div>
          <div className="haccp-eyebrow"><ClipboardCheck size={15} /> Sicurezza alimentare</div>
          <h1>Configurazione HACCP</h1>
          <p>Prepara e approva la configurazione per ciascun locale, in base al piano aziendale.</p>
        </div>
        <div className="haccp-header-mark" aria-hidden="true"><ShieldCheck size={29} /></div>
      </header>

      <aside className="haccp-notice">
        <span className="haccp-notice-icon"><FileText size={18} /></span>
        <p><strong>Solo le versioni attive sono operative.</strong> Le bozze e le revisioni non abilitano attività. I moduli di controllo si attiveranno quando saranno configurate e approvate le procedure specifiche del locale.</p>
      </aside>

      {error ? <HaccpError error={error} retry={reload} /> : locations.length === 0 ? (
        <section className="haccp-state-panel haccp-empty-state">
          <span className="haccp-state-icon"><Refrigerator size={22} /></span>
          <div>
            <h2>Nessun locale disponibile</h2>
            <p>Non risultano locali autorizzati da configurare per questo account.</p>
            <button type="button" className="haccp-button haccp-button-secondary mt-4" onClick={reload}>
              <RotateCcw size={16} /> Aggiorna
            </button>
          </div>
        </section>
      ) : (
        <>
          <section className="haccp-summary-strip" aria-label="Riepilogo configurazione">
            <div className="haccp-summary-icon"><CheckCircle2 size={21} /></div>
            <div className="haccp-summary-copy">
              <strong>{activeCount} di {locations.length} {locations.length === 1 ? 'locale' : 'locali'}</strong>
              <span>con configurazione approvata e attiva</span>
            </div>
            <span className="haccp-summary-separator" />
            <div className="haccp-summary-footnote">
              {pendingCount ? `${pendingCount} ${pendingCount === 1 ? 'bozza o revisione' : 'bozze o revisioni'} in corso` : 'Impostazioni separate per locale'}
            </div>
          </section>
          {actionError && <div className="haccp-save-alert is-conflict" role="alert">{actionError}</div>}
          <section className="haccp-location-section">
            <div className="haccp-section-heading">
              <div>
                <span className="haccp-section-kicker">I tuoi locali</span>
                <h2>Stato della configurazione</h2>
              </div>
              <span className="haccp-count">{locations.length} {locations.length === 1 ? 'locale' : 'locali'}</span>
            </div>
            <div className="haccp-location-list">
              {locations.map((location) => {
                const active = location.active_configuration;
                const pending = location.draft_configuration;
                const state = pending?.status || (active ? 'ACTIVE' : 'NOT_CONFIGURED');
                const modules = activeModuleNames(active);
                const equipmentCount = active?.equipment?.length || 0;
                const settingsUrl = `/impostazioni/haccp?location_id=${encodeURIComponent(location.location_id)}`;
                return (
                  <article className="haccp-location-card" key={location.location_id}>
                    <div className="haccp-location-card-main">
                      <div className="haccp-location-emblem"><Refrigerator size={20} /></div>
                      <div className="haccp-location-name">
                        <span>{location.company_name}</span>
                        <h3>{location.location_name}</h3>
                        <small>
                          {active
                            ? `Versione attiva ${active.configuration_version} · dal ${formatDate(active.effective_from)}`
                            : pending
                              ? `Bozza v${pending.configuration_version} · aggiornata ${formatDate(pending.updated_at)}`
                              : 'Nessuna configurazione approvata'}
                        </small>
                      </div>
                    </div>
                    <div className="haccp-location-meta">
                      <StateBadge state={state} />
                      {active ? (
                        <div className="haccp-mini-stats">
                          <span><Check size={14} /> {modules.length} {modules.length === 1 ? 'attività attiva' : 'attività attive'}</span>
                          <span><Refrigerator size={14} /> {equipmentCount} {equipmentCount === 1 ? 'attrezzatura' : 'attrezzature'}</span>
                        </div>
                      ) : (
                        <small className="haccp-muted-text">Nessuna attività operativa abilitata</small>
                      )}
                    </div>
                    <div className="haccp-location-actions">
                      {pending?.status === 'IN_REVIEW' && canApprove && (
                        <>
                          <button
                            type="button"
                            className="haccp-button haccp-button-primary"
                            disabled={busyId === location.location_id}
                            onClick={() => reviewAction(location, 'approve')}
                          >
                            {busyId === location.location_id ? 'Attendi…' : 'Approva e attiva'}
                          </button>
                          <button
                            type="button"
                            className="haccp-button haccp-button-secondary"
                            disabled={busyId === location.location_id}
                            onClick={() => reviewAction(location, 'return')}
                          >
                            Riporta in bozza
                          </button>
                        </>
                      )}
                      <Link className="haccp-button haccp-button-edit" to={settingsUrl}>
                        {pending?.status === 'DRAFT'
                          ? 'Modifica bozza'
                          : pending?.status === 'IN_REVIEW'
                            ? 'Vedi revisione'
                            : active
                              ? 'Nuova versione'
                              : 'Configura locale'}
                        <ArrowRight size={16} />
                      </Link>
                      {location.operational_controls_available && (
                        <Link
                          className="haccp-button haccp-button-primary"
                          to={`/haccp/temperature?location_id=${encodeURIComponent(location.location_id)}`}
                          data-testid={`haccp-temperature-open-${location.location_id}`}
                        >
                          <Thermometer size={15} /> Controlli temperatura
                        </Link>
                      )}
                      {active && modules.length > 0 && (
                        <span className="haccp-active-module-summary" data-testid={`haccp-active-modules-${location.location_id}`}>
                          Attività attive: {modules.join(' · ')}
                        </span>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        </>
      )}
    </main>
  );
}

function initialModules(configuration) {
  return new Set(
    (configuration?.modules || []).filter((module) => module.selected).map((module) => module.code),
  );
}

function initialEquipment(configuration) {
  return (configuration?.equipment || []).map((item, index) => ({
    ...item,
    _key: item.id || `existing-${index}`,
    number: item.number || '',
    label: item.label || '',
  }));
}

function nextEquipmentNumber(items, kind, omitKey = null) {
  const matching = items.filter((item) => item.kind === kind && item._key !== omitKey);
  const suffixes = matching.map((item) => String(item.number || '').match(/(\d+)$/))
    .filter(Boolean).map((match) => Number(match[1]));
  const prefixMatch = String(matching[0]?.number || '').match(/^(.*?)(\d+)$/);
  const prefix = prefixMatch?.[1] || '';
  return `${prefix}${Math.max(0, ...suffixes) + 1}`;
}

function EquipmentRow({
  item,
  onChange,
  onRemove,
  disabled,
  editing,
  onEdit,
  allowedKinds = EQUIPMENT_KINDS,
}) {
  const type = EQUIPMENT_KINDS.find((kind) => kind.value === item.kind);
  const Icon = type?.Icon || Coffee;
  return (
    <div className={`haccp-equipment-row ${editing ? 'is-editing' : ''}`}>
      <span className="haccp-equipment-icon"><Icon size={18} /></span>
      {editing ? (
        <>
          <label className="haccp-field">
            <span>Tipo attrezzatura</span>
            <select disabled={disabled || allowedKinds.length === 1} value={item.kind} onChange={(event) => onChange({ ...item, kind: event.target.value })} aria-label="Tipo attrezzatura">
              {allowedKinds.map((kind) => <option key={kind.value} value={kind.value}>{kind.label}</option>)}
            </select>
          </label>
          <label className="haccp-field haccp-number-field">
            <span>Numero assegnato</span>
            <input readOnly value={item.number} aria-label="Numero assegnato automaticamente" />
          </label>
          <label className="haccp-field haccp-label-field">
            <span>Nome <em>facoltativo</em></span>
            <input disabled={disabled} value={item.label} onChange={(event) => onChange({ ...item, label: event.target.value })} placeholder="Es. Banco cucina" />
          </label>
        </>
      ) : (
        <div className="haccp-equipment-summary">
          <strong>{item.label || `${type?.label || 'Attrezzatura'} ${item.number}`}</strong>
          <span>{type?.label || 'Attrezzatura'} · {item.number}</span>
        </div>
      )}
      <div className="haccp-equipment-actions">
        {!disabled && (
          editing ? <>
            <button type="button" className="haccp-button haccp-button-secondary haccp-equipment-done" onClick={onEdit}>Fine</button>
            <button type="button" className="haccp-icon-button" onClick={onRemove} aria-label="Rimuovi attrezzatura"><Trash2 size={17} /></button>
          </> : <button type="button" className="haccp-button haccp-button-edit" onClick={onEdit}>Modifica</button>
        )}
      </div>
    </div>
  );
}

function TemperatureRulesEditor({
  locationId,
  draft,
  modules,
  equipment,
  configDirty,
  onDirtyChange,
  onSaved,
}) {
  const me = useEffectiveMe();
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState('');
  const [templateMessage, setTemplateMessage] = useState('');
  const [templateNames, setTemplateNames] = useState({});
  const [selectedTemplateIds, setSelectedTemplateIds] = useState({});
  const [copySources, setCopySources] = useState({});
  const [advancedOpen, setAdvancedOpen] = useState({});
  const [simpleEditOpen, setSimpleEditOpen] = useState({});
  const [templateBusy, setTemplateBusy] = useState('');
  const [loadingRules, setLoadingRules] = useState(false);
  const [savingRules, setSavingRules] = useState(false);
  const [rulesError, setRulesError] = useState('');
  const [rulesMessage, setRulesMessage] = useState('');
  const [rulesDirty, setRulesDirty] = useState(false);
  const eligible = useMemo(() => equipment.filter((item) => (
    item.id && ['FRIDGE', 'FREEZER', 'COLD_ROOM'].includes(item.kind)
  )), [equipment]);
  const selected = modules.has('TEMPERATURES');
  const readOnly = draft?.status !== 'DRAFT';
  const canManageTemplates = (me?.role_codes || []).some(
    (role) => ['CEO', 'TITOLARE', 'RESP_GEN'].includes(role),
  );

  useEffect(() => {
    let cancelled = false;
    if (!locationId || !selected) {
      setTemplates([]);
      return undefined;
    }
    setTemplateLoading(true);
    setTemplateError('');
    getHaccpTemperatureTemplates(locationId)
      .then((response) => {
        if (!cancelled) setTemplates(Array.isArray(response?.templates) ? response.templates : []);
      })
      .catch(() => {
        if (!cancelled) setTemplateError('Template aziendali non disponibili. Puoi configurare i valori manualmente.');
      })
      .finally(() => {
        if (!cancelled) setTemplateLoading(false);
      });
    return () => { cancelled = true; };
  }, [locationId, selected, draft?.id]);

  useEffect(() => {
    let cancelled = false;
    if (!draft?.id || !selected) {
      setRules([]);
      onDirtyChange(false);
      return undefined;
    }
    setLoadingRules(true);
    setRulesError('');
    getHaccpTemperatureRules(locationId, draft.id)
      .then((response) => {
        if (cancelled) return;
        const byEquipment = new Map((response.rules || []).map((rule) => [rule.equipment_id, rule]));
        const initialRules = eligible.map((item) => {
          const current = byEquipment.get(item.id);
          const isFridge = item.kind === 'FRIDGE';
          const isFreezer = item.kind === 'FREEZER';
          return {
            equipment_id: item.id,
            equipment_name: item.label || `${item.kind} ${item.number}`,
            isProposed: !current && (isFridge || isFreezer),
            limit_mode: current?.limit_mode || (isFreezer ? 'MAX_ONLY' : 'RANGE'),
            min_temperature_c: current
              ? current.min_temperature_c ?? ''
              : (isFridge ? '2' : ''),
            max_temperature_c: current
              ? current.max_temperature_c ?? ''
              : (isFridge ? '5' : isFreezer ? '-18' : ''),
            tolerance_c: current?.tolerance_c || '',
            profile_code: current?.profile_code || '',
            operational_target_min_temperature_c: current
              ? current.operational_target_min_temperature_c || ''
              : (isFreezer ? '-22' : isFridge ? '2' : ''),
            operational_target_max_temperature_c: current
              ? current.operational_target_max_temperature_c || ''
              : (isFreezer ? '-18' : isFridge ? '5' : ''),
            limit_basis: current
              ? current.limit_basis || ''
              : (isFridge || isFreezer ? 'COMPANY_STANDARD' : ''),
            basis_reference: current
              ? current.basis_reference || ''
              : (isFridge || isFreezer
                ? 'Valore aziendale proposto; verificare con il piano HACCP e le indicazioni applicabili.'
                : ''),
            check_times_text: (current?.check_times_local || []).join(', '),
            late_after_minutes: current?.late_after_minutes || '',
            instructions: current?.instructions || '',
            anomaly_note_required: current?.anomaly_note_required ?? true,
            measurement_method: current?.measurement_method || '',
            corrective_instructions: current?.corrective_instructions || '',
            verification_after_minutes: current?.verification_after_minutes || '',
          };
        });
        const hasUnconfiguredRule = initialRules.some((rule) => rule.isProposed);
        setRules(initialRules);
        onDirtyChange(hasUnconfiguredRule);
        setRulesDirty(hasUnconfiguredRule);
        setSelectedTemplateIds({});
        setCopySources({});
        setAdvancedOpen({});
        setSimpleEditOpen({});
        setTemplateNames({});
        setTemplateMessage('');
      })
      .catch(() => {
        if (!cancelled) setRulesError('Parametri temperatura non disponibili. Ricarica la bozza.');
      })
      .finally(() => {
        if (!cancelled) setLoadingRules(false);
      });
    return () => { cancelled = true; };
  }, [draft?.id, draft?.revision, eligible, locationId, selected, onDirtyChange]);

  const updateRule = (equipmentId, patch) => {
    setRules((current) => current.map((rule) => (
      rule.equipment_id === equipmentId ? { ...rule, ...patch } : rule
    )));
    onDirtyChange(true);
    setRulesDirty(true);
    setRulesMessage('');
  };

  const applyTemplate = (equipmentId, template, source = 'template') => {
    const currentRule = rules.find((item) => item.equipment_id === equipmentId);
    const targetKind = eligible.find((item) => item.id === equipmentId)?.kind;
    const templateProfile = TEMPERATURE_PROFILE_BY_CODE[template.profile_code];
    if (
      (currentRule?.profile_code && template.profile_code !== currentRule.profile_code)
      || (templateProfile?.equipmentKind && templateProfile.equipmentKind !== targetKind)
    ) {
      setTemplateError('Il profilo del template non è compatibile con la selezione e l’attrezzatura.');
      return;
    }
    setTemplateError('');
    updateRule(equipmentId, {
      limit_mode: template.limit_mode,
      min_temperature_c: template.min_temperature_c ?? '',
      max_temperature_c: template.max_temperature_c ?? '',
      tolerance_c: template.tolerance_c ?? '',
      profile_code: template.profile_code || currentRule?.profile_code || '',
      operational_target_min_temperature_c: (
        template.operational_target_min_temperature_c
        ?? currentRule?.operational_target_min_temperature_c ?? ''
      ),
      operational_target_max_temperature_c: (
        template.operational_target_max_temperature_c
        ?? currentRule?.operational_target_max_temperature_c ?? ''
      ),
      limit_basis: template.limit_basis || currentRule?.limit_basis || '',
      basis_reference: template.basis_reference ?? currentRule?.basis_reference ?? '',
      check_times_text: (template.check_times_local || []).join(', '),
      late_after_minutes: template.late_after_minutes,
      instructions: template.instructions || '',
      anomaly_note_required: template.anomaly_note_required,
      measurement_method: template.measurement_method,
      corrective_instructions: template.corrective_instructions,
      verification_after_minutes: template.verification_after_minutes,
    });
    setTemplateMessage(source === 'equipment'
      ? 'Parametri copiati nella bozza da un’altra attrezzatura. Controllali prima di salvare.'
      : 'Valori copiati come regola indipendente nella bozza del locale. Controllali prima di salvare e inviare in approvazione.');
  };

  const selectProfile = (rule, profileCode) => {
    const profile = TEMPERATURE_PROFILE_BY_CODE[profileCode];
    const compatibleTemplates = templates.filter((template) => (
      template.equipment_kind === eligible.find((item) => item.id === rule.equipment_id)?.kind
      && template.profile_code === profileCode
    ));
    updateRule(rule.equipment_id, {
      profile_code: profileCode,
      limit_basis: profile?.preset.limit_basis || '',
      basis_reference: profile?.preset.basis_reference || '',
    });
    setSelectedTemplateIds((current) => ({
      ...current,
      [rule.equipment_id]: compatibleTemplates.length === 1 ? compatibleTemplates[0].id : '',
    }));
    setAdvancedOpen((current) => ({
      ...current,
      [rule.equipment_id]: true,
    }));
  };

  const applyProfileSuggestion = (rule) => {
    const profile = TEMPERATURE_PROFILE_BY_CODE[rule.profile_code];
    if (!profile || readOnly) return;
    const selectedTemplate = templates.find(
      (template) => template.id === selectedTemplateIds[rule.equipment_id]
        && template.profile_code === profile.code,
    );
    const matchingTemplates = templates.filter((template) => (
      template.profile_code === profile.code
      && template.equipment_kind === eligible.find((item) => item.id === rule.equipment_id)?.kind
    ));
    if (selectedTemplate || matchingTemplates.length === 1) {
      applyTemplate(rule.equipment_id, selectedTemplate || matchingTemplates[0]);
      return;
    }
    updateRule(rule.equipment_id, {
      ...profile.preset,
      profile_code: profile.code,
    });
    setAdvancedOpen((current) => ({
      ...current,
      [rule.equipment_id]: true,
    }));
    setTemplateMessage(profile.needsPlanValue
      ? 'Profilo pesce impostato. Completa il limite numerico usando il piano HACCP approvato.'
      : 'Impostazione proposta copiata nella bozza. Verificala e inviala per approvazione.');
  };

  const copyFromEquipment = (equipmentId) => {
    const source = rules.find((item) => item.equipment_id === copySources[equipmentId]);
    if (!source) return;
    const target = rules.find((item) => item.equipment_id === equipmentId);
    const targetKind = eligible.find((item) => item.id === equipmentId)?.kind;
    const sourceProfile = TEMPERATURE_PROFILE_BY_CODE[source.profile_code];
    if (sourceProfile && sourceProfile.equipmentKind && sourceProfile.equipmentKind !== targetKind) {
      setTemplateError('Il profilo della sorgente non è compatibile con questa attrezzatura.');
      return;
    }
    applyTemplate(equipmentId, source, 'equipment');
  };

  const saveCompanyTemplate = async (rule, updateExisting = false) => {
    if (!canManageTemplates || readOnly || templateBusy) return;
    const selectedTemplate = templates.find(
      (item) => item.id === selectedTemplateIds[rule.equipment_id],
    );
    const name = updateExisting ? selectedTemplate?.name : templateNames[rule.equipment_id]?.trim();
    if (!name) {
      setTemplateError('Inserisci un nome per il template aziendale.');
      return;
    }
    if (updateExisting && !window.confirm(
      `Aggiornare il template “${name}”? Le configurazioni locali già salvate non cambieranno.`,
    )) return;
    const item = eligible.find((candidate) => candidate.id === rule.equipment_id);
    if (!item) return;
    setTemplateBusy(rule.equipment_id);
    setTemplateError('');
    setTemplateMessage('');
    try {
      const payload = {
        name,
        equipment_kind: item.kind,
        profile_code: rule.profile_code || null,
        ...temperatureRulePayload(rule),
      };
      const response = updateExisting
        ? await updateHaccpTemperatureTemplate(
          locationId, selectedTemplate.id, { ...payload, expected_revision: selectedTemplate.revision },
        )
        : await createHaccpTemperatureTemplate(locationId, payload);
      const savedTemplate = response?.template;
      if (!savedTemplate?.id) throw new Error('INVALID_TEMPLATE_RESPONSE');
      setTemplates((current) => [
        ...current.filter((existing) => existing.id !== savedTemplate.id),
        savedTemplate,
      ].sort((left, right) => left.name.localeCompare(right.name, 'it')));
      setSelectedTemplateIds((current) => ({ ...current, [rule.equipment_id]: savedTemplate.id }));
      setTemplateMessage(updateExisting
        ? 'Template aziendale aggiornato. Le regole locali salvate restano invariate.'
        : 'Template aziendale creato. I valori locali restano una copia indipendente.');
    } catch (error) {
      setTemplateError(error?.status === 409
        ? 'Nome già utilizzato o template aggiornato da un altro responsabile. Ricarica e riprova.'
        : error?.status === 422
          ? 'Completa i parametri richiesti per creare o aggiornare il template.'
          : 'Template non salvato. Verifica la connessione e riprova.');
    } finally {
      setTemplateBusy('');
    }
  };

  const saveRules = async () => {
    if (!draft || !selected || savingRules || configDirty || !rules.every(isTemperatureRuleComplete)) return;
    setSavingRules(true);
    setRulesError('');
    setRulesMessage('');
    try {
      const payload = rules.map((rule) => ({
        equipment_id: rule.equipment_id,
        ...temperatureRulePayload(rule),
      }));
      await saveHaccpTemperatureRules(locationId, draft.id, draft.revision, payload);
      onDirtyChange(false);
      setRulesDirty(false);
      await onSaved();
      setRulesMessage('Temperature salvate nella bozza.');
    } catch (error) {
      setRulesError(error?.status === 409
        ? 'La bozza è stata aggiornata. Ricarica prima di salvare.'
        : error?.status === 422
          ? 'Completa i limiti, gli orari e le attrezzature richieste.'
          : 'Non è stato possibile salvare le regole. Riprova.');
    } finally {
      setSavingRules(false);
    }
  };

  return (
    <section className="haccp-editor-section" data-testid="haccp-temperature-rules-editor">
      <div className="haccp-section-heading">
        <div>
          <span className="haccp-section-kicker">Controlli del locale</span>
          <h2>Temperature delle attrezzature</h2>
          <p>Imposta i valori previsti per ogni frigorifero, congelatore o cella.</p>
        </div>
      </div>
      {!selected ? (
        <p className="haccp-temperature-rule-note">Seleziona l’attività Temperature per definire i controlli.</p>
      ) : eligible.length !== equipment.filter((item) => ['FRIDGE', 'FREEZER', 'COLD_ROOM'].includes(item.kind)).length ? (
        <p className="haccp-temperature-rule-note">Salva prima l’elenco delle attrezzature; poi potrai impostare i relativi controlli.</p>
      ) : loadingRules ? (
        <p className="haccp-temperature-rule-note" role="status">Caricamento delle regole…</p>
      ) : eligible.length === 0 ? (
        <p className="haccp-temperature-rule-note">Aggiungi almeno un frigorifero, congelatore o cella e salvalo prima di inviare la bozza.</p>
      ) : (
        <>
          <p className="haccp-temperature-rule-note">Le proposte si possono modificare e diventano operative solo dopo revisione e approvazione.</p>
          <div className="haccp-temperature-rule-list">
            {rules.map((rule) => (
              <article className="haccp-temperature-rule-card" key={rule.equipment_id}>
                <h3>{rule.equipment_name}</h3>
                <p className="haccp-temperature-rule-equipment">
                  {EQUIPMENT_KINDS.find((kind) => kind.value === eligible.find(
                    (item) => item.id === rule.equipment_id,
                  )?.kind)?.label || 'Attrezzatura'}
                  {' · '}
                  {eligible.find((item) => item.id === rule.equipment_id)?.number}
                </p>
                {(() => {
                  const equipmentKind = eligible.find((item) => item.id === rule.equipment_id)?.kind;
                  const profile = TEMPERATURE_PROFILE_BY_CODE[rule.profile_code];
                  const profileOptions = profilesForEquipment(equipmentKind);
                  const matchingTemplates = templates.filter((template) => (
                    template.equipment_kind === equipmentKind
                    && template.profile_code === rule.profile_code
                  ));
                  const selectedTemplateId = selectedTemplateIds[rule.equipment_id] || '';
                  const hasSelectedProfileTemplate = matchingTemplates.some(
                    (template) => template.id === selectedTemplateId,
                  );
                  const minimum = formatTemperature(rule.min_temperature_c);
                  const maximum = formatTemperature(rule.max_temperature_c);
                  const editsFreezerWorkRange = rule.limit_mode === 'MAX_ONLY'
                    && Boolean(rule.operational_target_min_temperature_c)
                    && Boolean(rule.operational_target_max_temperature_c);
                  const simpleEditIsOpen = Boolean(simpleEditOpen[rule.equipment_id]);
                  const needsAdvancedSetup = !isTemperatureRuleComplete(rule);
                  return (
                    <>
                      <div className="haccp-temperature-simple wide">
                        <div className="haccp-temperature-simple-summary">
                          <span>Temperatura impostata</span>
                            <strong>{temperatureRuleDisplay(rule)}</strong>
                          {rule.isProposed && (
                            <small>Proposta iniziale: verifica che sia adatta al piano del locale.</small>
                          )}
                          {rule.profile_code === 'FISH' && !minimum && !maximum && (
                            <small>Valori da impostare in base al piano e al prodotto.</small>
                          )}
                        </div>
                        {!readOnly && (
                          <button
                            type="button"
                            className="haccp-button haccp-button-edit"
                            aria-expanded={simpleEditIsOpen}
                            onClick={() => setSimpleEditOpen((current) => ({
                              ...current,
                              [rule.equipment_id]: !current[rule.equipment_id],
                            }))}
                          >
                            {simpleEditIsOpen ? 'Chiudi' : 'Modifica temperature'}
                          </button>
                        )}
                      </div>
                      {simpleEditIsOpen && !readOnly && (
                        <div className="haccp-temperature-simple-editor wide">
                          {(rule.limit_mode !== 'MAX_ONLY' || editsFreezerWorkRange) && (
                            <label className="haccp-field">
                              <span>Minima °C</span>
                              <input
                                type="number"
                                step="0.1"
                                aria-label="Temperatura minima °C"
                                value={editsFreezerWorkRange
                                  ? rule.operational_target_min_temperature_c : rule.min_temperature_c}
                                onChange={(event) => updateRule(rule.equipment_id, editsFreezerWorkRange
                                  ? { operational_target_min_temperature_c: event.target.value }
                                  : {
                                    min_temperature_c: event.target.value,
                                    ...(rule.operational_target_min_temperature_c === rule.min_temperature_c
                                      ? { operational_target_min_temperature_c: event.target.value }
                                      : {}),
                                  })}
                              />
                            </label>
                          )}
                          {rule.limit_mode !== 'MIN_ONLY' && (
                            <label className="haccp-field">
                              <span>Massima °C</span>
                              <input
                                type="number"
                                step="0.1"
                                aria-label="Temperatura massima °C"
                                value={editsFreezerWorkRange
                                  ? rule.operational_target_max_temperature_c : rule.max_temperature_c}
                                onChange={(event) => updateRule(rule.equipment_id, editsFreezerWorkRange
                                  ? { operational_target_max_temperature_c: event.target.value }
                                  : {
                                    max_temperature_c: event.target.value,
                                    ...(rule.operational_target_max_temperature_c === rule.max_temperature_c
                                      ? { operational_target_max_temperature_c: event.target.value }
                                      : {}),
                                  })}
                              />
                            </label>
                          )}
                          <label className="haccp-field">
                            <span>Tolleranza °C <em>facoltativa</em></span>
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              value={rule.tolerance_c}
                              onChange={(event) => updateRule(rule.equipment_id, {
                                tolerance_c: event.target.value,
                              })}
                            />
                          </label>
                        </div>
                      )}
                      {needsAdvancedSetup && !readOnly && (
                        <div className="haccp-temperature-setup-needed wide" role="status">
                          <span>Completa le informazioni richieste dal piano del locale prima di inviare la configurazione.</span>
                          <button
                            type="button"
                            className="haccp-config-open"
                            onClick={() => setAdvancedOpen((current) => ({
                              ...current,
                              [rule.equipment_id]: true,
                            }))}
                          >
                            Completa configurazione <ArrowRight size={16} />
                          </button>
                        </div>
                      )}
                      <details
                        className="haccp-temperature-advanced wide"
                        open={Boolean(advancedOpen[rule.equipment_id])}
                        onToggle={(event) => {
                          const isOpen = event.currentTarget.open;
                          setAdvancedOpen((current) => (
                            current[rule.equipment_id] === isOpen
                              ? current : { ...current, [rule.equipment_id]: isOpen }
                          ));
                        }}
                      >
                        <summary>Impostazioni avanzate</summary>
                        <div className="haccp-temperature-advanced-content">
                {templateLoading && <p className="haccp-temperature-rule-note" role="status">Caricamento impostazioni aziendali…</p>}
                {templateError && <p className="haccp-temperature-rule-error" role="alert">{templateError}</p>}
                {templateMessage && <p className="haccp-temperature-success" role="status"><CheckCircle2 size={16} />{templateMessage}</p>}
                <div className="haccp-temperature-profile-panel wide">
                  <label className="haccp-field">
                    <span>Tipo di alimento</span>
                    <select
                      aria-label={`Profilo HACCP ${rule.equipment_name}`}
                      value={rule.profile_code}
                      disabled={readOnly}
                      onChange={(event) => selectProfile(rule, event.target.value)}
                    >
                      <option value="">Seleziona il tipo</option>
                      {profileOptions.map((item) => (
                        <option value={item.code} key={item.code}>{item.label}</option>
                      ))}
                    </select>
                  </label>
                  {profile ? (
                    <div className="haccp-temperature-profile-summary">
                      <strong>{profile.summary}</strong>
                      <p>{profile.note}</p>
                      {rule.min_temperature_c !== '' || rule.max_temperature_c !== '' ? (
                        <small>Valori attuali: {temperatureRuleSummary(rule)}</small>
                      ) : null}
                    </div>
                  ) : (
                    <p className="haccp-temperature-rule-note">
                      {rule.min_temperature_c || rule.max_temperature_c
                        ? `Configurazione attuale: ${temperatureRuleSummary(rule)}`
                        : 'Seleziona un tipo per vedere eventuali proposte.'}
                    </p>
                  )}
                  {profile && matchingTemplates.length > 1 && (
                    <label className="haccp-field">
                      <span>Impostazione aziendale</span>
                      <select
                        aria-label={`Impostazione aziendale ${rule.equipment_name}`}
                        value={selectedTemplateId}
                        disabled={readOnly}
                        onChange={(event) => setSelectedTemplateIds((current) => ({
                          ...current, [rule.equipment_id]: event.target.value,
                        }))}
                      >
                        <option value="">Scegli un’impostazione</option>
                        {matchingTemplates.map((template) => (
                          <option value={template.id} key={template.id}>{template.name}</option>
                        ))}
                      </select>
                    </label>
                  )}
                  {profile && (
                    <button
                      type="button"
                      className="haccp-button haccp-button-primary wide"
                      disabled={readOnly || (matchingTemplates.length > 1 && !hasSelectedProfileTemplate)}
                      onClick={() => applyProfileSuggestion(rule)}
                    >
                      {profile.needsPlanValue
                        ? 'Imposta dal piano HACCP'
                        : profile.needsManualValues
                          ? 'Apri configurazione manuale'
                          : 'Applica proposta alla bozza'}
                    </button>
                  )}
                </div>
                <div className="haccp-temperature-template-tools wide">
                  <label className="haccp-field wide">
                    <span>Template aziendale · valori da verificare</span>
                    <select
                      value={selectedTemplateIds[rule.equipment_id] || ''}
                      disabled={readOnly || !templates.some((template) => template.equipment_kind === eligible.find((item) => item.id === rule.equipment_id)?.kind)}
                      onChange={(event) => {
                        const templateId = event.target.value;
                        setSelectedTemplateIds((current) => ({ ...current, [rule.equipment_id]: templateId }));
                        const template = templates.find((item) => item.id === templateId);
                        if (template) applyTemplate(rule.equipment_id, template);
                      }}
                    >
                      <option value="">Nessun template selezionato</option>
                      {templates.filter((template) => template.equipment_kind === eligible.find((item) => item.id === rule.equipment_id)?.kind).map((template) => (
                        React.createElement(
                          'option',
                          { value: template.id, key: template.id },
                          `${template.name} · ${TEMPERATURE_PROFILE_BY_CODE[template.profile_code]?.label || 'Configurazione precedente'} · ${temperatureModeLabel(template.limit_mode)}`,
                        )
                      ))}
                    </select>
                  </label>
                  {rules.length > 1 && (
                    <div className="haccp-temperature-copy-controls">
                      <label className="haccp-field">
                        <span>Copia da un’altra attrezzatura</span>
                        <select
                          value={copySources[rule.equipment_id] || ''}
                          disabled={readOnly}
                          onChange={(event) => setCopySources((current) => ({
                            ...current, [rule.equipment_id]: event.target.value,
                          }))}
                        >
                          <option value="">Scegli attrezzatura</option>
                          {rules.filter((candidate) => candidate.equipment_id !== rule.equipment_id).map((candidate) => (
                            <option value={candidate.equipment_id} key={candidate.equipment_id}>{candidate.equipment_name}</option>
                          ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        className="haccp-button haccp-button-secondary"
                        disabled={readOnly || !copySources[rule.equipment_id]}
                        onClick={() => copyFromEquipment(rule.equipment_id)}
                      >
                        Copia parametri
                      </button>
                    </div>
                  )}
                  {canManageTemplates && (
                    <div className="haccp-temperature-template-actions">
                      <label className="haccp-field">
                        <span>Nome del template aziendale</span>
                        <input
                          maxLength={120}
                          value={templateNames[rule.equipment_id] || ''}
                          disabled={readOnly || !!templateBusy}
                          placeholder="Es. Frigo cucina"
                          onChange={(event) => setTemplateNames((current) => ({
                            ...current, [rule.equipment_id]: event.target.value,
                          }))}
                        />
                      </label>
                      <button
                        type="button"
                        className="haccp-button haccp-button-secondary"
                        disabled={readOnly || !!templateBusy || !rule.profile_code || !templateNames[rule.equipment_id]?.trim()}
                        onClick={() => saveCompanyTemplate(rule)}
                      >
                        {templateBusy === rule.equipment_id ? 'Salvataggio…' : 'Crea template aziendale'}
                      </button>
                      {selectedTemplateIds[rule.equipment_id] && (
                        <button
                          type="button"
                          className="haccp-button haccp-button-secondary"
                          disabled={readOnly || !!templateBusy || !rule.profile_code}
                          onClick={() => saveCompanyTemplate(rule, true)}
                        >
                          Aggiorna template selezionato
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <label className="haccp-field wide">
                  <span>Modalità del limite</span>
                  <select
                    value={rule.limit_mode}
                    disabled={readOnly}
                    onChange={(event) => {
                      const nextMode = event.target.value;
                      updateRule(rule.equipment_id, {
                        limit_mode: nextMode,
                        ...(nextMode === 'MAX_ONLY' ? { min_temperature_c: '' } : {}),
                        ...(nextMode === 'MIN_ONLY' ? { max_temperature_c: '' } : {}),
                      });
                    }}
                  >
                    <option value="RANGE">Intervallo · minimo e massimo</option>
                    <option value="MAX_ONLY">Solo limite massimo</option>
                    <option value="MIN_ONLY">Solo limite minimo</option>
                  </select>
                </label>
                <label className="haccp-field">
                  <span>Base del limite</span>
                  <select
                    value={rule.limit_basis}
                    disabled={readOnly}
                    onChange={(event) => updateRule(rule.equipment_id, { limit_basis: event.target.value })}
                  >
                    <option value="">Seleziona la fonte</option>
                    {TEMPERATURE_LIMIT_BASES.map((basis) => (
                      <option value={basis.value} key={basis.value}>{basis.label}</option>
                    ))}
                  </select>
                </label>
                <label className="haccp-field">
                  <span>
                    Riferimento della fonte
                    {rule.limit_basis === 'LEGAL_SPECIFIC' ? ' · obbligatorio' : ' · facoltativo'}
                  </span>
                  <input
                    maxLength={500}
                    value={rule.basis_reference}
                    disabled={readOnly}
                    placeholder={rule.limit_basis === 'LEGAL_SPECIFIC'
                      ? 'Indica norma e riferimento applicabile'
                      : 'Piano, prodotto o nota aziendale'}
                    onChange={(event) => updateRule(rule.equipment_id, { basis_reference: event.target.value })}
                  />
                </label>
                {rule.limit_mode !== 'MAX_ONLY' && (
                  <label className="haccp-field">
                    <span>Minimo °C</span>
                    <input type="number" step="0.1" required={!readOnly} value={rule.min_temperature_c} disabled={readOnly} onChange={(event) => updateRule(rule.equipment_id, { min_temperature_c: event.target.value })} />
                  </label>
                )}
                {rule.limit_mode !== 'MIN_ONLY' && (
                  <label className="haccp-field">
                    <span>Massimo °C</span>
                    <input type="number" step="0.1" required={!readOnly} value={rule.max_temperature_c} disabled={readOnly} onChange={(event) => updateRule(rule.equipment_id, { max_temperature_c: event.target.value })} />
                  </label>
                )}
                <label className="haccp-field">
                  <span>Target operativo minimo °C <em>facoltativo</em></span>
                  <input
                    type="number"
                    step="0.1"
                    value={rule.operational_target_min_temperature_c}
                    disabled={readOnly}
                    onChange={(event) => updateRule(rule.equipment_id, {
                      operational_target_min_temperature_c: event.target.value,
                    })}
                  />
                </label>
                <label className="haccp-field">
                  <span>Target operativo massimo °C <em>facoltativo</em></span>
                  <input
                    type="number"
                    step="0.1"
                    value={rule.operational_target_max_temperature_c}
                    disabled={readOnly}
                    onChange={(event) => updateRule(rule.equipment_id, {
                      operational_target_max_temperature_c: event.target.value,
                    })}
                  />
                </label>
                <label className="haccp-field">
                  <span>Tolleranza °C <em>facoltativa</em></span>
                  <input type="number" min="0" step="0.1" value={rule.tolerance_c} disabled={readOnly} onChange={(event) => updateRule(rule.equipment_id, { tolerance_c: event.target.value })} />
                </label>
                <label className="haccp-field">
                  <span>Minuti prima del ritardo</span>
                  <input type="number" min="1" max="1440" step="1" value={rule.late_after_minutes} disabled={readOnly} onChange={(event) => updateRule(rule.equipment_id, { late_after_minutes: event.target.value })} />
                </label>
                <label className="haccp-field wide">
                  <span>Orari locali <em>HH:MM, separati da virgola</em></span>
                  <input value={rule.check_times_text} disabled={readOnly} placeholder="Es. 08:00, 14:00" onChange={(event) => updateRule(rule.equipment_id, { check_times_text: event.target.value })} />
                </label>
                <label className="haccp-field wide">
                  <span>Metodo previsto dal piano</span>
                  <select value={rule.measurement_method} disabled={readOnly} onChange={(event) => updateRule(rule.equipment_id, { measurement_method: event.target.value })}>
                    <option value="">Seleziona il metodo</option>
                    <option value="DISPLAY">Lettura del display</option>
                    <option value="PROBE">Misurazione con sonda</option>
                  </select>
                </label>
                <label className="haccp-field wide">
                  <span>Procedura in caso di anomalia</span>
                  <textarea maxLength={1000} rows={2} value={rule.corrective_instructions} disabled={readOnly} onChange={(event) => updateRule(rule.equipment_id, { corrective_instructions: event.target.value })} />
                </label>
                <label className="haccp-field wide">
                  <span>Verifica successiva dopo quanti minuti</span>
                  <input type="number" min="1" max="1440" value={rule.verification_after_minutes} disabled={readOnly} onChange={(event) => updateRule(rule.equipment_id, { verification_after_minutes: event.target.value })} />
                </label>
                <label className="haccp-field wide">
                  <span>Istruzioni <em>facoltative</em></span>
                  <textarea maxLength={1000} rows={2} value={rule.instructions} disabled={readOnly} onChange={(event) => updateRule(rule.equipment_id, { instructions: event.target.value })} />
                </label>
                <label className="haccp-temperature-checkline wide">
                  <input type="checkbox" checked={rule.anomaly_note_required} disabled={readOnly} onChange={(event) => updateRule(rule.equipment_id, { anomaly_note_required: event.target.checked })} />
                  Richiedi una nota per le anomalie
                </label>
                        </div>
                      </details>
                    </>
                  );
                })()}
              </article>
            ))}
          </div>
          {configDirty && <p className="haccp-temperature-rule-note" role="status">Salva prima le attività e le attrezzature; poi salva questi parametri.</p>}
          {rulesError && <p className="haccp-temperature-rule-error" role="alert">{rulesError}</p>}
          {rulesMessage && <p className="haccp-temperature-success" role="status"><CheckCircle2 size={16} />{rulesMessage}</p>}
          {!readOnly && (
            <div className="haccp-temperature-rule-actions">
              <span className="haccp-readonly-note">{rules.length} attrezzature configurabili</span>
              <button type="button" className="haccp-button haccp-button-secondary" disabled={!rules.length || !selected || !eligible.length || !rulesDirty || configDirty || savingRules || !rules.every(isTemperatureRuleComplete)} onClick={saveRules}>
                <Save size={15} /> {savingRules ? 'Salvataggio…' : 'Salva temperature'}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export function HaccpSettings() {
  const { locations, loading, error, reload } = useHaccpConfigurations();
  const [searchParams, setSearchParams] = useSearchParams();
  const me = useEffectiveMe();
  const canApprove = canApproveHaccp(me);
  const requestedId = searchParams.get('location_id');
  const selectedLocation = useMemo(
    () => locations.find((item) => String(item.location_id) === String(requestedId)) || locations[0],
    [locations, requestedId],
  );
  const draft = selectedLocation?.draft_configuration || null;
  const [activeModule, setActiveModule] = useState(searchParams.get('module') || '');
  const [editingEquipmentKey, setEditingEquipmentKey] = useState(null);
  const [modules, setModules] = useState(new Set());
  const [equipment, setEquipment] = useState([]);
  const [newEquipmentKind, setNewEquipmentKind] = useState('FRIDGE');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedMessage, setSavedMessage] = useState('');
  const [dirty, setDirty] = useState(false);
  const [temperatureRulesDirty, setTemperatureRulesDirty] = useState(false);
  const [positiveRulesDirty, setPositiveRulesDirty] = useState(false);
  const [negativeRulesDirty, setNegativeRulesDirty] = useState(false);
  const anyBlastRulesDirty = positiveRulesDirty || negativeRulesDirty;
  const [reviewNote, setReviewNote] = useState('');

  useEffect(() => {
    setModules(initialModules(draft));
    setEquipment(initialEquipment(draft));
    setSaveError(null);
    setSavedMessage('');
    setDirty(false);
    setTemperatureRulesDirty(false);
    setPositiveRulesDirty(false);
    setNegativeRulesDirty(false);
    setReviewNote(draft?.review_note || '');
    if (selectedLocation && String(selectedLocation.location_id) !== requestedId) {
      setSearchParams({ location_id: String(selectedLocation.location_id) }, { replace: true });
    }
  }, [draft, selectedLocation, requestedId, setSearchParams]);

  const editable = draft?.status === 'DRAFT';
  const currentModule = MODULES.find((item) => item.code === activeModule);
  const equipmentKindsForModule = activeModule === 'TEMPERATURES'
    ? ['FRIDGE', 'FREEZER', 'COLD_ROOM']
    : ['BLAST_CHILLER'];
  const visibleEquipment = equipment.filter((item) => equipmentKindsForModule.includes(item.kind));
  const allowedEquipmentKinds = EQUIPMENT_KINDS.filter((kind) => (
    equipmentKindsForModule.includes(kind.value)
  ));
  const openModule = (code) => {
    setActiveModule(code);
    setNewEquipmentKind(code === 'TEMPERATURES' ? 'FRIDGE' : 'BLAST_CHILLER');
    const params = new URLSearchParams(searchParams);
    params.set('module', code);
    if (selectedLocation) params.set('location_id', String(selectedLocation.location_id));
    setSearchParams(params);
  };
  const closeModule = () => {
    setActiveModule('');
    const params = new URLSearchParams(searchParams);
    params.delete('module');
    if (selectedLocation) params.set('location_id', String(selectedLocation.location_id));
    setSearchParams(params);
  };
  const toggleModule = (code) => {
    if (!editable || anyBlastRulesDirty) return;
    setModules((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
    setDirty(true);
    setSavedMessage('');
  };

  const updateEquipment = (key, nextItem) => {
    if (anyBlastRulesDirty) return;
    setEquipment((items) => items.map((item) => {
      if (item._key !== key) return item;
      const kindChanged = item.kind !== nextItem.kind;
      return {
        ...nextItem,
        number: kindChanged && !item.id
          ? nextEquipmentNumber(items, nextItem.kind, key)
          : item.number,
      };
    }));
    setDirty(true);
    setSavedMessage('');
  };

  const withAction = async (operation, successText) => {
    if (!selectedLocation || saving) return;
    setSaving(true);
    setSaveError(null);
    setSavedMessage('');
    try {
      await operation();
      await reload();
      setDirty(false);
      setSavedMessage(successText);
    } catch (requestError) {
      setSaveError(requestError);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateDraft = () => withAction(
    () => createHaccpDraft(selectedLocation.location_id),
    'Bozza creata.',
  );

  const handleSave = (event) => {
    event.preventDefault();
    if (!draft || !editable || anyBlastRulesDirty || dirty === false && savedMessage) return;
    return withAction(
      () => saveHaccpConfiguration(
        selectedLocation.location_id,
        draft.id,
        draft.revision,
        Array.from(modules),
        equipment,
      ),
      'Bozza salvata.',
    );
  };

  const handleSubmit = () => {
    if (anyBlastRulesDirty) return;
    return withAction(
      () => submitHaccpConfiguration(selectedLocation.location_id, draft.id, draft.revision, reviewNote || null),
      'Bozza inviata per approvazione.',
    );
  };

  const handleApproval = (operation, message) => withAction(
    () => operation(selectedLocation.location_id, draft.id, draft.revision, reviewNote || null),
    message,
  );

  const isConflict = saveError?.status === 409;
  const errorText = isConflict
    ? 'Ricarica la configurazione più recente prima di apportare altre modifiche.'
    : 'Controlla la connessione e riprova. Se il problema continua, contatta l’amministratore.';

  if (loading) return <HaccpLoading />;
  if (error) {
    return <main className="haccp-page" data-testid="haccp-settings"><Link to="/impostazioni/haccp/stato" className="haccp-back-link"><ArrowLeft size={16} /> Panoramica configurazione</Link><HaccpError error={error} retry={reload} /></main>;
  }
  if (!locations.length) {
    return <main className="haccp-page" data-testid="haccp-settings"><Link to="/impostazioni/haccp/stato" className="haccp-back-link"><ArrowLeft size={16} /> Panoramica configurazione</Link><section className="haccp-state-panel haccp-empty-state"><span className="haccp-state-icon"><Refrigerator size={22} /></span><div><h2>Nessun locale disponibile</h2><p>Non risultano locali autorizzati da configurare per questo account.</p><button type="button" className="haccp-button haccp-button-secondary mt-4" onClick={reload}><RotateCcw size={16} /> Aggiorna</button></div></section></main>;
  }

  return (
    <main className="haccp-page haccp-settings-page" data-testid="haccp-settings">
      <Link to="/impostazioni/haccp/stato" className="haccp-back-link"><ArrowLeft size={16} /> Panoramica configurazione</Link>
      <header className="haccp-page-header haccp-settings-header">
        <div>
          <div className="haccp-eyebrow"><ClipboardCheck size={15} /> Impostazioni del locale</div>
          <h1>{draft ? `Configurazione v${draft.configuration_version}` : 'Configurazione HACCP'}</h1>
      <p>Organizza le procedure di questo locale. Le modifiche restano in bozza finché non vengono approvate.</p>
        </div>
        <div className="haccp-header-mark" aria-hidden="true"><ShieldCheck size={29} /></div>
      </header>

      <div className="haccp-selector-line">
        <label className="haccp-field haccp-location-select">
          <span>Locale</span>
          <span className="haccp-select-wrap">
            <select value={String(selectedLocation.location_id)} onChange={(event) => {
              setActiveModule('');
              setSearchParams({ location_id: event.target.value });
            }}>
              {locations.map((location) => (
                <option
                  key={location.location_id}
                  value={location.location_id}
                  label={`${location.company_name} · ${location.location_name}`}
                />
              ))}
            </select>
            <ChevronDown size={16} aria-hidden="true" />
          </span>
        </label>
        <StateBadge state={draft?.status || (selectedLocation.active_configuration ? 'ACTIVE' : 'NOT_CONFIGURED')} />
      </div>

      <aside className="haccp-notice">
        <span className="haccp-notice-icon"><FileText size={18} /></span>
        <p><strong>Versioni e approvazioni.</strong> Le modifiche vengono salvate come bozza. Solo una versione approvata diventa attiva; le versioni precedenti restano nello storico.</p>
      </aside>

      {saveError && (
        <div className={`haccp-save-alert ${isConflict ? 'is-conflict' : ''}`} role="alert">
          <span className="haccp-save-alert-icon"><AlertCircle size={19} /></span>
          <div className="haccp-save-alert-copy">
            <strong>{isConflict ? 'La configurazione è stata aggiornata altrove' : 'Operazione non riuscita'}</strong>
            <p>{errorText}</p>
          </div>
          <button type="button" className="haccp-button haccp-button-conflict" onClick={reload}>
            <RotateCcw size={15} /> Ricarica
          </button>
        </div>
      )}

      {!draft ? (
        <section className="haccp-state-panel" data-testid="haccp-no-draft">
          <span className="haccp-state-icon"><FileText size={21} /></span>
          <div>
            <h2>{selectedLocation.active_configuration ? 'Prepara una nuova versione' : 'Nessuna bozza presente'}</h2>
            <p>{selectedLocation.active_configuration
              ? `La versione attiva è la ${selectedLocation.active_configuration.configuration_version}. La nuova bozza partirà da questa configurazione.`
              : 'Crea una bozza per definire le attività e le attrezzature di questo locale.'}</p>
            <button
              type="button"
              className="haccp-button haccp-button-primary mt-4"
              onClick={handleCreateDraft}
              disabled={saving}
            >
              <Plus size={16} /> {saving ? 'Creazione…' : 'Crea bozza'}
            </button>
          </div>
        </section>
      ) : (
        <form onSubmit={handleSave}>
          {draft.status === 'IN_REVIEW' ? (
            <aside className="haccp-notice haccp-review-notice" data-testid="haccp-review-state">
              <span className="haccp-notice-icon"><ShieldCheck size={18} /></span>
              <p><strong>In revisione.</strong> Questa versione non è modificabile e non è ancora operativa.</p>
            </aside>
          ) : (
            <aside className="haccp-notice">
              <span className="haccp-notice-icon"><FileText size={18} /></span>
              <p><strong>Salvataggio come bozza.</strong> Non attiva controlli operativi e non indica uno stato di conformità.</p>
            </aside>
          )}

          {!currentModule ? (
            <section className="haccp-editor-section haccp-config-landing" aria-label="Moduli di configurazione">
              <div className="haccp-section-heading">
                <div><span className="haccp-section-kicker">Procedure del locale</span><h2>Configurazione</h2><p>Apri una procedura per consultare o modificare i relativi dettagli.</p></div>
                {editable && <span className="haccp-count">{MODULES.filter(({ code }) => modules.has(code)).length} attive</span>}
              </div>
              <div className="haccp-module-grid">
                {MODULES.map(({ code, title, detail, Icon }) => {
                  const checked = modules.has(code);
                  return (
                    <article className={`haccp-config-card ${checked ? 'is-selected' : ''}`} key={code}>
                      <div className="haccp-config-card-top">
                        <span className="haccp-module-icon"><Icon size={20} /></span>
                        <span className={`haccp-config-status ${checked ? 'is-selected' : ''}`}>{checked ? 'Attiva' : 'Non attiva'}</span>
                      </div>
                      <h3>{title}</h3>
                      <p>{detail}</p>
                      <button type="button" className="haccp-config-open" onClick={() => openModule(code)}>
                        Apri configurazione <ArrowRight size={16} />
                      </button>
                    </article>
                  );
                })}
              </div>
            </section>
          ) : (
            <section className="haccp-module-detail">
              <button type="button" className="haccp-back-link haccp-back-button" onClick={closeModule}>
                <ArrowLeft size={16} /> Tutte le configurazioni
              </button>
              <div className="haccp-module-detail-heading">
                <div className="haccp-module-icon"><currentModule.Icon size={21} /></div>
                <div><span className="haccp-section-kicker">Configurazione del locale</span><h2>{currentModule.title}</h2></div>
                {editable && (
                  <button
                    type="button"
                    className={`haccp-module-toggle ${modules.has(activeModule) ? 'is-enabled' : ''}`}
                    aria-pressed={modules.has(activeModule)}
                    disabled={anyBlastRulesDirty}
                    onClick={() => toggleModule(activeModule)}
                  >
                    {modules.has(activeModule) ? 'Attività attiva' : 'Attiva attività'}
                  </button>
                )}
              </div>
              <p className="haccp-module-detail-description">{currentModule.detail}</p>
            </section>
          )}

          {['TEMPERATURES', 'BLAST_CHILLING_POSITIVE', 'BLAST_CHILLING_NEGATIVE'].includes(activeModule) && <section className="haccp-editor-section haccp-equipment-section">
            <div className="haccp-section-heading">
              <div>
                <span className="haccp-section-kicker">Attrezzature</span>
                <h2>{activeModule === 'TEMPERATURES' ? 'Frigoriferi e congelatori' : 'Abbattitori'}</h2>
                <p>{activeModule === 'TEMPERATURES'
                  ? 'Controlli associati alle attrezzature del freddo.'
                  : 'Attrezzature usate per le procedure di abbattimento.'}</p>
              </div>
              {editable && <span className="haccp-count">{visibleEquipment.length} {visibleEquipment.length === 1 ? 'elemento' : 'elementi'}</span>}
            </div>
            {visibleEquipment.length > 0 && (
              <div className="haccp-equipment-list">
                {visibleEquipment.map((item) => (
                  <EquipmentRow
                    key={item._key}
                    item={item}
                    disabled={!editable || anyBlastRulesDirty}
                    editing={editingEquipmentKey === item._key}
                    allowedKinds={allowedEquipmentKinds}
                    onEdit={() => setEditingEquipmentKey((current) => current === item._key ? null : item._key)}
                    onChange={(nextItem) => updateEquipment(item._key, nextItem)}
                    onRemove={() => {
                      setEquipment((items) => items.filter((entry) => entry._key !== item._key));
                      setDirty(true);
                      setSavedMessage('');
                    }}
                  />
                ))}
              </div>
            )}
            {editable && (
              <div className="haccp-add-equipment-row">
                {activeModule === 'TEMPERATURES' && (
                  <label className="haccp-field haccp-new-equipment-kind">
                    <span>Tipo</span>
                    <select
                      value={newEquipmentKind}
                      disabled={anyBlastRulesDirty}
                      onChange={(event) => setNewEquipmentKind(event.target.value)}
                    >
                      {allowedEquipmentKinds.map((kind) => (
                        <option key={kind.value} value={kind.value}>{kind.label}</option>
                      ))}
                    </select>
                  </label>
                )}
                <button
                  type="button"
                  className="haccp-add-equipment"
                  disabled={anyBlastRulesDirty}
                  onClick={() => {
                    const kind = activeModule === 'TEMPERATURES' ? newEquipmentKind : 'BLAST_CHILLER';
                    const key = `new-${Date.now()}-${equipment.length}`;
                    const number = nextEquipmentNumber(equipment, kind);
                    setEquipment((items) => [...items, {
                      _key: key, kind, number, label: '',
                    }]);
                    setEditingEquipmentKey(key);
                    setDirty(true);
                    setSavedMessage('');
                  }}
                >
                  <Plus size={17} /> Aggiungi attrezzatura
                </button>
              </div>
            )}
          </section>}

          {draft && activeModule === 'TEMPERATURES' && (
            <TemperatureRulesEditor
              locationId={selectedLocation.location_id}
              draft={draft}
              modules={modules}
              equipment={equipment}
              configDirty={dirty}
              onDirtyChange={setTemperatureRulesDirty}
              onSaved={reload}
            />
          )}
          {draft && activeModule === 'BLAST_CHILLING_POSITIVE' && (
            <PositiveBlastChillingRulesEditor
              locationId={selectedLocation.location_id}
              draft={draft}
              modules={modules}
              equipment={equipment}
              configDirty={dirty}
              onDirtyChange={setPositiveRulesDirty}
              onSaved={reload}
            />
          )}
          {draft && activeModule === 'BLAST_CHILLING_NEGATIVE' && (
            <NegativeBlastChillingRulesEditor
              locationId={selectedLocation.location_id}
              draft={draft}
              modules={modules}
              equipment={equipment}
              configDirty={dirty}
              onDirtyChange={setNegativeRulesDirty}
              onSaved={reload}
            />
          )}

          {draft.status === 'IN_REVIEW' && canApprove && (
            <section className="haccp-editor-section" data-testid="haccp-review-actions">
              <label className="haccp-field">
                <span>Nota di revisione <em>facoltativa</em></span>
                <input maxLength={500} value={reviewNote} onChange={(event) => setReviewNote(event.target.value)} placeholder="Aggiungi una nota per lo storico" />
              </label>
            </section>
          )}

          <footer className="haccp-save-footer">
            <div className="haccp-save-status" aria-live="polite">
              {savedMessage && <><CheckCircle2 size={17} /> {savedMessage}</>}
              {positiveRulesDirty && <span>Salva prima la procedura positiva.</span>}
              {negativeRulesDirty && <span>Salva prima la procedura negativa.</span>}
            </div>
            <div className="haccp-save-actions">
              <Link to="/impostazioni/haccp/stato" className="haccp-button haccp-button-cancel">Torna alla panoramica</Link>
              {editable ? (
                <>
                  <button type="submit" className="haccp-button haccp-button-secondary" disabled={saving || isConflict || anyBlastRulesDirty}>
                    {saving ? 'Salvataggio…' : <><Save size={17} /> Salva bozza</>}
                  </button>
                  <button
                    type="button"
                    className="haccp-button haccp-button-primary"
                    disabled={saving || dirty || temperatureRulesDirty || anyBlastRulesDirty || isConflict}
                    onClick={handleSubmit}
                  >
                    Invia per approvazione <ArrowRight size={16} />
                  </button>
                </>
              ) : draft.status === 'IN_REVIEW' && canApprove ? (
                <>
                  <button
                    type="button"
                    className="haccp-button haccp-button-secondary"
                    disabled={saving}
                    onClick={() => handleApproval(returnHaccpConfiguration, 'Bozza restituita al configuratore.')}
                  >
                    Riporta in bozza
                  </button>
                  <button
                    type="button"
                    className="haccp-button haccp-button-primary"
                    disabled={saving}
                    onClick={() => handleApproval(approveHaccpConfiguration, 'Configurazione approvata e attiva.')}
                  >
                    {saving ? 'Attendi…' : 'Approva e attiva'}
                  </button>
                </>
              ) : (
                <span className="haccp-readonly-note">Versione in sola lettura</span>
              )}
            </div>
          </footer>
        </form>
      )}
    </main>
  );
}

export default HaccpOverview;
